import { useFeedback } from "@taunggyi-plus/auth";
import { useEffect, useState, type FormEvent } from "react";
import { useAuth, usePagedRecords } from "@taunggyi-plus/auth";
import {
  DetailCard,
  StatusPill,
  ManagementListToolbar,
  PaginatedList,
  ServerPagination,
} from "@taunggyi-plus/design-system";
export function DirectDeliveryForm({
  fleet,
  onSubmitted,
}: {
  fleet: string;
  onSubmitted?: () => void;
}) {
  const { client } = useAuth();
  const [message, setMessage] = useFeedback(),
    [busy, setBusy] = useState(false),
    [id, setId] = useState(() => crypto.randomUUID());
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!client || busy) return;
    const form = e.currentTarget,
      f = new FormData(form);
    setBusy(true);
    try {
      const r = await client.rpc("request_direct_delivery", {
        fleet,
        pickup: f.get("pickup"),
        dropoff: f.get("dropoff"),
        recipient: f.get("recipient"),
        phone: f.get("phone"),
        note: f.get("note"),
        request_id: id,
      });
      if (r.error) throw new Error(r.error.message);
      setMessage(
        "Request sent. Open Orders to review the provider’s delivery fee before accepting.",
      );
      setId(crypto.randomUUID());
      form.reset();
      onSubmitted?.();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Request failed. Please retry.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <form className="form-grid" onSubmit={submit}>
      <p>The provider will quote a delivery fee. Confirm it in Orders before dispatch.</p>
      {[
        ["pickup", "Pickup address", 5, 500],
        ["dropoff", "Drop-off address", 5, 500],
        ["recipient", "Recipient name", 2, 120],
        ["phone", "Recipient phone", 6, 30],
      ].map(([key, label, min, max]) => (
        <label className="field" key={key}>
          {label}
          <input name={String(key)} minLength={Number(min)} maxLength={Number(max)} required />
        </label>
      ))}
      <label className="field">
        Package details and instructions
        <textarea name="note" maxLength={1000} />
      </label>
      {message && <p role="status">{message}</p>}
      <button className="button" disabled={busy}>
        Request delivery quote
      </button>
      <a href="/orders">Track requests</a>
    </form>
  );
}
type Request = {
  id: string;
  status: string;
  pickup: string;
  dropoff: string;
  recipient: string;
  phone: string;
  note: string;
  quote_minor: number | null;
  response: string;
  job_status: string | null;
  created_at: string;
};
export function DirectDeliveryOrders({ provider = false }: { provider?: boolean }) {
  const { client } = useAuth();
  const [search, setSearch] = useState(""),
    [status, setStatus] = useState("all"),
    [message, setMessage] = useFeedback(),
    [busy, setBusy] = useState(false);
  const records = usePagedRecords<Request>("delivery", search, status);
  const load = records.refresh;
  useEffect(() => {
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [load]);
  async function act(id: string, decision: string, quote: number | null, note = "") {
    if (!client || busy) return;
    setBusy(true);
    try {
      const r = await client.rpc("respond_direct_delivery", { request: id, decision, quote, note });
      if (r.error) throw new Error(r.error.message);
      await load();
      setMessage("Request updated.");
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Could not update request.");
    } finally {
      setBusy(false);
    }
  }
  const filtered = records.items;
  return (
    <section>
      <h2>Delivery requests</h2>
      {message && <p role="status">{message}</p>}
      <ManagementListToolbar
        query={search}
        onQueryChange={setSearch}
        searchLabel="Search delivery requests"
        resultCount={records.total}
        filters={[
          {
            label: "Status",
            value: status,
            onChange: setStatus,
            options: ["all", "pending", "quoted", "accepted", "rejected", "cancelled"].map(
              (value) => ({ value, label: value === "all" ? "All statuses" : value }),
            ),
          },
        ]}
        onClear={() => {
          setSearch("");
          setStatus("all");
        }}
      />
      <p role="status">{records.error || (records.loading ? "Loading…" : "")}</p>

      <PaginatedList
        pagination="external"
        items={filtered}
        resetKey={`${search}:${status}`}
        label="Delivery request pages"
        renderItem={(r) => (
          <DetailCard
            key={r.id}
            title={`${r.pickup} → ${r.dropoff}`}
            summary={<StatusPill>{r.job_status ?? r.status}</StatusPill>}
          >
            <strong>
              {r.pickup} → {r.dropoff}
            </strong>
            <p>{r.job_status ?? r.status}</p>
            <p>
              {r.recipient} · <a href={`tel:${r.phone.replace(/[^+0-9]/g, "")}`}>{r.phone}</a>
            </p>
            <p>{r.note}</p>
            {r.response && <p>{r.response}</p>}
            {r.quote_minor && <p>Delivery fee: {(r.quote_minor / 100).toLocaleString()} MMK</p>}
            {provider && r.status === "pending" && (
              <form
                className="form-grid"
                onSubmit={(e) => {
                  e.preventDefault();
                  const f = new FormData(e.currentTarget);
                  void act(r.id, "quoted", Math.round(Number(f.get("fee")) * 100));
                }}
              >
                <label className="field">
                  Delivery fee (MMK)
                  <input type="number" name="fee" min="0.01" step="0.01" required />
                </label>
                <button className="button" disabled={busy}>
                  Send quote
                </button>
                <button
                  className="button button--secondary"
                  type="button"
                  disabled={busy}
                  onClick={() => void act(r.id, "rejected", null)}
                >
                  Decline request
                </button>
              </form>
            )}
            {!provider && r.status === "quoted" && (
              <button
                className="button"
                disabled={busy}
                onClick={() => void act(r.id, "accepted", null)}
              >
                Accept fee & request dispatch
              </button>
            )}
            {!provider && ["pending", "quoted"].includes(r.status) && (
              <button
                className="button button--secondary"
                disabled={busy}
                onClick={() => void act(r.id, "cancelled", null)}
              >
                Cancel request
              </button>
            )}
          </DetailCard>
        )}
      />
      {records.total > 5 && (
        <ServerPagination
          page={records.page}
          total={records.total}
          onPage={records.setPage}
          loading={records.loading}
        />
      )}
    </section>
  );
}

export function CustomerDeliveryRequest({ onSubmitted }: { onSubmitted: () => void }) {
  const { client, user } = useAuth();
  const [fleets, setFleets] = useState<{ id: string; display_name: string }[]>([]);
  const [fleet, setFleet] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!client || !user) return;
    let active = true;
    void client
      .from("delivery_fleets")
      .select("id,display_name")
      .eq("is_active", true)
      .then(({ data, error }) => {
        if (!active) return;
        setLoading(false);
        if (error) setError(error.message);
        else setFleets((data ?? []) as { id: string; display_name: string }[]);
      });
    return () => {
      active = false;
    };
  }, [client, user]);
  if (!user) return <p>Please sign in to request delivery.</p>;
  if (loading) return <p role="status">Loading delivery providers…</p>;
  if (error) return <p role="alert">{error}</p>;
  if (!fleets.length) return <p>No active delivery providers are available.</p>;
  return (
    <div className="record-detail-popup__content">
      <label className="field">
        <span>Delivery provider</span>
        <select value={fleet} onChange={(event) => setFleet(event.target.value)}>
          <option value="">Choose a provider</option>
          {fleets.map((item) => (
            <option key={item.id} value={item.id}>
              {item.display_name}
            </option>
          ))}
        </select>
      </label>
      {fleet && <DirectDeliveryForm key={fleet} fleet={fleet} onSubmitted={onSubmitted} />}
    </div>
  );
}
